import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CampaignSingleSelectSummaryField } from '@fmnts/api/next';
import { logger } from '@fmnts/common/log';
import { isDefined, isString } from '@fmnts/core';
import {
  AbstractDeclarativeFieldComponent,
  FormDeclarationError,
  SelectInputItem,
  ensureTemplate,
  isFormSelectField,
  isTemplateExpression,
  splitTemplate,
} from '@fmnts/declarative-forms';
import { InlineTranslateable } from '@fmnts/i18n';
import {
  Observable,
  filter,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  throwError,
} from 'rxjs';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';

@Component({
  selector: 'next-radio-group-summary',
  templateUrl: './radio-group-summary-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule],
})
export class RadioGroupSummaryFieldComponent
  extends AbstractDeclarativeFieldComponent<CampaignSingleSelectSummaryField>
  implements OnInit
{
  private readonly _log = logger({
    name: RadioGroupSummaryFieldComponent.name,
    category: 'declarative-forms.field',
  });

  /**
   * Observable that emits with the content for this summary field
   */
  public content$!: Observable<InlineTranslateable>;

  ngOnInit() {
    const { defaultValue } = this.field.widgetOptions;

    if (isDefined(defaultValue) && !isString(defaultValue)) {
      this.content$ = this._getContentFromField$([defaultValue].flat());
    }

    if (isString(defaultValue)) {
      this.content$ = of(defaultValue).pipe(
        // Ensure that for any emitted value that references a field
        // that it is transformed into templating form of `{{some-field-key}}`
        map((v) => ensureTemplate(v, this.declaration)),
        map(splitTemplate),
        mergeMap((parts) => {
          // We only support single template expressions, and do not
          // interpolate over literals + expressions.
          const p = parts[0];

          if (!isTemplateExpression(p)) {
            return throwError(
              () =>
                new FormDeclarationError({
                  message: 'Not a valid expression',
                }),
            );
          }

          return of(p.expression);
        }),
        filter(isDefined),
        switchMap((fieldReference) =>
          this._getContentFromReference$(fieldReference),
        ),
      );
    }
  }

  /**
   * Map items from form-declaration to checkbox-summary
   *
   * @param defaultValue
   * @returns
   */
  private _getContentFromField$(
    defaultValue: SelectInputItem[],
  ): Observable<InlineTranslateable> {
    if (defaultValue.length > 1) {
      return throwError(
        () =>
          new FormDeclarationError({
            message: `Cannot have more than one selected item`,
          }),
      );
    }
    return of(defaultValue[0].label);
  }

  private _getContentFromReference$(
    defaultValue: string,
  ): Observable<InlineTranslateable> {
    const selectField = this.declaration.findByKey(
      defaultValue,
      isFormSelectField,
    );

    if (!selectField) {
      return throwError(
        () =>
          new FormDeclarationError({
            message: `Cannot find referenced field: {{${defaultValue}}}`,
          }),
      );
    }

    return this.form.flatValueChanges.pipe(
      startWith(this.form.flatValue),
      map((v) => v?.[defaultValue]),
      map((value) =>
        selectField.widgetOptions.items.find(
          (option) => `${option.value}` === `${value}`,
        ),
      ),
      map((item) => item?.label || ''),
    );
  }
}
